import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { EnterAuth, EnterAuthStatus, EnterAuthUser } from '../types';
import {
  consumeWarmGatewaySession,
  fetchGatewayAccessToken,
  fetchGatewaySession,
  GatewaySessionRequestError,
  GatewaySilentLoginRequiredError,
  GatewayUnauthorizedError,
} from './session-client';
import { buildEnterSilentLoginStartPath } from './silent-login/protocol';
import { createEnterAuthTraceId } from './auth-trace';
import { completeLoginSession, ensureLoginSession } from './login-session';

interface EnterAuthSessionContextValue extends EnterAuth {
  getAccessToken(): Promise<string>;
  refreshSession(includeAccessToken?: boolean): Promise<EnterAuthUser | undefined>;
}

interface CachedAccessToken {
  accessToken: string;
  revalidateAfter: number;
  hardExpiresAt: number;
}

interface SessionState {
  status: EnterAuthStatus;
  error?: Error;
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: EnterAuthUser;
  loginSessionId?: string;
}

interface PendingRetryDelay {
  timerId: number;
  resolve(shouldContinue: boolean): void;
}

const EnterAuthSessionContext = createContext<EnterAuthSessionContextValue | null>(null);
const TOKEN_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000;
const SESSION_RETRY_DELAYS_MS = [500, 1_500, 3_500] as const;
const SESSION_MAX_ATTEMPTS = SESSION_RETRY_DELAYS_MS.length + 1;
const INITIAL_SESSION_STATE: SessionState = {
  status: 'bootstrapping',
  isAuthenticated: false,
  isLoading: true,
};

function broadcastAuthEvent(event: 'unauthorized'): void {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }

  const channel = new BroadcastChannel('enter-auth');
  channel.postMessage(event);
  channel.close();
}

function createCachedAccessToken(accessToken: string, expiresAt: number): CachedAccessToken | undefined {
  const hardExpiresAt = expiresAt - TOKEN_EXPIRY_SKEW_MS;
  if (hardExpiresAt <= Date.now()) {
    return undefined;
  }

  return {
    accessToken,
    revalidateAfter: Date.now() + TOKEN_CACHE_MAX_AGE_MS,
    hardExpiresAt,
  };
}

function isRetryableSessionError(error: unknown): boolean {
  return (
    error instanceof GatewaySessionRequestError &&
    ['network', 'aborted', 'server', 'invalid_response'].includes(error.category)
  );
}

function createAnonymousState(error?: Error): SessionState {
  return {
    status: 'anonymous',
    error,
    isAuthenticated: false,
    isLoading: false,
  };
}

function createAuthenticatedState(user: EnterAuthUser, loginSessionId?: string): SessionState {
  return {
    status: 'authenticated',
    isAuthenticated: true,
    isLoading: false,
    user,
    ...(loginSessionId ? { loginSessionId } : {}),
  };
}

export function EnterAuthSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<SessionState>(INITIAL_SESSION_STATE);
  const stateRef = useRef<SessionState>(INITIAL_SESSION_STATE);
  const mountedRef = useRef(true);
  const pendingRefreshRef = useRef<Promise<EnterAuthUser | undefined> | null>(null);
  const activeSessionRequestRef = useRef<AbortController | null>(null);
  const pendingRetryDelayRef = useRef<PendingRetryDelay | null>(null);
  const pendingTokenRef = useRef<Promise<string> | null>(null);
  const cachedTokenRef = useRef<CachedAccessToken | null>(null);
  const tokenVersionRef = useRef(0);
  const sessionGenerationRef = useRef(0);
  const sessionUserRef = useRef<string | undefined>(undefined);

  const commitState = useCallback((nextState: SessionState) => {
    stateRef.current = nextState;
    if (mountedRef.current) {
      setState(nextState);
    }
  }, []);

  const clearAccessToken = useCallback(() => {
    tokenVersionRef.current += 1;
    cachedTokenRef.current = null;
    pendingTokenRef.current = null;
  }, []);

  const abortActiveSessionRequest = useCallback(() => {
    activeSessionRequestRef.current?.abort();
    activeSessionRequestRef.current = null;
  }, []);

  const cancelRetryDelay = useCallback(() => {
    const pendingDelay = pendingRetryDelayRef.current;
    if (!pendingDelay) {
      return;
    }
    window.clearTimeout(pendingDelay.timerId);
    pendingRetryDelayRef.current = null;
    pendingDelay.resolve(false);
  }, []);

  const waitForRetry = useCallback(
    (delayMs: number, generation: number): Promise<boolean> =>
      new Promise(resolve => {
        const timerId = window.setTimeout(() => {
          pendingRetryDelayRef.current = null;
          resolve(mountedRef.current && sessionGenerationRef.current === generation);
        }, delayMs);
        pendingRetryDelayRef.current = { timerId, resolve };
      }),
    []
  );

  const establishAnonymousSession = useCallback(
    (error?: Error) => {
      sessionGenerationRef.current += 1;
      pendingRefreshRef.current = null;
      abortActiveSessionRequest();
      cancelRetryDelay();
      sessionUserRef.current = undefined;
      clearAccessToken();
      commitState(createAnonymousState(error));
    },
    [abortActiveSessionRequest, cancelRetryDelay, clearAccessToken, commitState]
  );

  const refreshSession = useCallback(
    (includeAccessToken = false): Promise<EnterAuthUser | undefined> => {
      if (pendingRefreshRef.current) {
        return pendingRefreshRef.current;
      }

      const generation = sessionGenerationRef.current + 1;
      sessionGenerationRef.current = generation;
      const establishedState =
        stateRef.current.status === 'authenticated' || stateRef.current.status === 'anonymous'
          ? stateRef.current
          : undefined;

      if (!establishedState && stateRef.current.status !== 'bootstrapping') {
        commitState({
          status: 'recovering',
          isAuthenticated: false,
          isLoading: true,
        });
      }

      const request = (async (): Promise<EnterAuthUser | undefined> => {
        for (let attempt = 1; attempt <= SESSION_MAX_ATTEMPTS; attempt += 1) {
          if (!mountedRef.current || sessionGenerationRef.current !== generation) {
            return undefined;
          }

          try {
            const requestController = new AbortController();
            activeSessionRequestRef.current = requestController;
            let session: Awaited<ReturnType<typeof fetchGatewaySession>>;
            try {
              // 首次 bootstrap 优先复用入口预热（warmGatewaySessionForBootstrap）
              // 发起的在途请求；不存在或已被消费时按原路径自发加载。
              const warmSession =
                attempt === 1 && includeAccessToken ? consumeWarmGatewaySession() : undefined;
              session = warmSession
                ? await warmSession
                : await fetchGatewaySession({
                    includeAccessToken,
                    signal: requestController.signal,
                  });
            } finally {
              if (activeSessionRequestRef.current === requestController) {
                activeSessionRequestRef.current = null;
              }
            }
            if (!mountedRef.current || sessionGenerationRef.current !== generation) {
              return undefined;
            }

            if (session.status === 'anonymous') {
              sessionUserRef.current = undefined;
              clearAccessToken();
              commitState(createAnonymousState());
              return undefined;
            }

            if (sessionUserRef.current !== session.user.sub) {
              clearAccessToken();
            }
            if (session.accessToken && session.expiresAt) {
              const cachedToken = createCachedAccessToken(session.accessToken, session.expiresAt);
              if (cachedToken) {
                cachedTokenRef.current = cachedToken;
              }
            }
            sessionUserRef.current = session.user.sub;
            completeLoginSession(session.user.sub);
            commitState(
              createAuthenticatedState(session.user, ensureLoginSession(session.user.sub))
            );
            return session.user;
          } catch (error) {
            if (!mountedRef.current || sessionGenerationRef.current !== generation) {
              return undefined;
            }

            if (error instanceof GatewaySilentLoginRequiredError) {
              sessionUserRef.current = undefined;
              clearAccessToken();
              commitState({
                status: 'recovering',
                isAuthenticated: false,
                isLoading: true,
              });
              if (typeof window !== 'undefined') {
                const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                window.location.assign(
                  buildEnterSilentLoginStartPath(returnTo, createEnterAuthTraceId())
                );
              }
              return undefined;
            }

            const retryDelayMs = SESSION_RETRY_DELAYS_MS[attempt - 1];
            if (retryDelayMs !== undefined && isRetryableSessionError(error)) {
              if (!establishedState) {
                commitState({
                  status: 'recovering',
                  error: error instanceof Error ? error : undefined,
                  isAuthenticated: false,
                  isLoading: true,
                });
              }
              if (!(await waitForRetry(retryDelayMs, generation))) {
                return undefined;
              }
              continue;
            }

            if (establishedState) {
              commitState(establishedState);
            } else {
              commitState({
                status: 'degraded',
                error: error instanceof Error ? error : new Error('Failed to read Gateway session'),
                isAuthenticated: false,
                isLoading: true,
              });
            }
            return undefined;
          }
        }
        return undefined;
      })();

      pendingRefreshRef.current = request;
      const clearPendingRequest = () => {
        if (pendingRefreshRef.current === request) {
          pendingRefreshRef.current = null;
        }
      };
      void request.then(clearPendingRequest, clearPendingRequest);
      return request;
    },
    [clearAccessToken, commitState, waitForRetry]
  );

  const requestAccessToken = useCallback((): Promise<string> => {
    if (pendingTokenRef.current) {
      return pendingTokenRef.current;
    }

    const tokenVersion = tokenVersionRef.current;
    let request: Promise<string>;

    request = fetchGatewayAccessToken()
      .then(({ accessToken, expiresAt }) => {
        if (tokenVersion === tokenVersionRef.current) {
          cachedTokenRef.current = createCachedAccessToken(accessToken, expiresAt) ?? null;
        }
        return accessToken;
      })
      .catch((error: unknown) => {
        if (error instanceof GatewayUnauthorizedError) {
          establishAnonymousSession(error);
          broadcastAuthEvent('unauthorized');
        }
        throw error;
      })
      .finally(() => {
        if (pendingTokenRef.current === request) {
          pendingTokenRef.current = null;
        }
      });

    pendingTokenRef.current = request;
    return request;
  }, [establishAnonymousSession]);

  const getAccessToken = useCallback((): Promise<string> => {
    const cachedToken = cachedTokenRef.current;
    const now = Date.now();

    if (cachedToken && cachedToken.hardExpiresAt > now) {
      if (cachedToken.revalidateAfter <= now) {
        void requestAccessToken().catch(() => undefined);
      }
      return Promise.resolve(cachedToken.accessToken);
    }

    return requestAccessToken();
  }, [requestAccessToken]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      pendingRefreshRef.current = null;
      abortActiveSessionRequest();
      cancelRetryDelay();
      clearAccessToken();
    };
  }, [abortActiveSessionRequest, cancelRetryDelay, clearAccessToken]);

  useEffect(() => {
    void refreshSession(true);
  }, [refreshSession]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshSession();
      }
    };
    const onOnline = () => void refreshSession();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [refreshSession]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel('enter-auth');
    channel.onmessage = () => establishAnonymousSession();
    return () => channel.close();
  }, [establishAnonymousSession]);

  const value = useMemo<EnterAuthSessionContextValue>(
    () => ({ ...state, getAccessToken, refreshSession }),
    [getAccessToken, refreshSession, state]
  );

  return <EnterAuthSessionContext.Provider value={value}>{children}</EnterAuthSessionContext.Provider>;
}

export function useEnterAuthSession(): EnterAuthSessionContextValue {
  const context = useContext(EnterAuthSessionContext);
  if (!context) {
    throw new Error('Enter auth hooks must be used within EnterAuthProvider');
  }
  return context;
}
